import type { MutableRefObject, Ref } from 'react';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import type { icModel } from 'generated-ts-common';
import type { PanelProps } from '../basic';
import { Button, Collapsible, Panel, Tool } from '../basic';
import {
  convertNumberToFileSize,
  isEmptyArray,
  isEmptyString
} from '../../util';
import type { DistributiveOmit } from '../../lib';
import {
  Breakpoints,
  DefaultDateFormatter,
  DefaultDateTimeFormatter,
  intl,
  intlLanguageCode,
  useBreakpoint
} from '../../lib';
import type {
  ChildrenProp,
  ClassNameProp,
  VoidCallback
} from '../common-types';
import DOMPurify from 'dompurify';

type DocumentInformation = icModel.saf.infrastructure.model.document.DocumentInformation;
type GenCode = icModel.saf.infrastructure.model.svs.GenCode;
type PersonName = icModel.saf.infrastructure.model.patient.PersonName;
type SimplePatient = icModel.saf.infrastructure.model.patient.SimplePatient;
type Institution = icModel.saf.infrastructure.model.document.Institution;
type ExtraMetadata = icModel.saf.infrastructure.model.document.ExtraMetadata;
type ReferenceID = icModel.saf.infrastructure.model.document.ReferenceID;

/**
 * Retrieves the text that's supposed to be displayed when a value is empty.
 *
 * @return the 'view.documentmetadata.label.notspecified' language string
 */
const getNotSpecified = (): string => {
  return intl('view.documentmetadata.label.notspecified');
};

/**
 * Renderer for GenCode values.
 *
 * @param value the data value
 * @return the correct string value for the given value
 */
const genCodeRenderer = (value: GenCode | undefined): string => {
  if (!value) {
    return getNotSpecified();
  }
  if (!isEmptyString(value.displayName)) {
    return value.displayName;
  } else {
    return value.value;
  }
};

/**
 * Renderer for regular (string, number, etc.) values.
 *
 * @param value the data value
 * @return the correct string value for the given value
 */
const dataRenderer = (value: string | number | undefined): string => {
  if (!value || (typeof value === 'string' && isEmptyString(value))) {
    return getNotSpecified();
  }

  if (typeof value === 'number') {
    value = value.toString();
  }

  return value;
};

/**
 * Renderer for the language code.
 *
 * @param languageCode the language code to render
 * @return the resolved localized language string for the given language code
 */
const languageCodeRenderer = (languageCode: string | undefined): string => {
  if (isEmptyString(languageCode)) {
    return getNotSpecified();
  }
  return intlLanguageCode(languageCode, window.SenseConstants?.locale);
};

/**
 * Renderer for PersonName values.
 *
 * @param value the data value
 * @return the correct string value for the given value
 */
const personNameRenderer = (value: PersonName | undefined): string => {
  if (!value) {
    return getNotSpecified();
  }

  return `${value.prefix || ''}  ${value.familyName || ''} ${
    value.givenName || ''
  }`.trim();
};

/**
 * Renderer for data with a name property in the form of a PersonName.
 *
 * @param value the data value
 * @return the correct string value for the given value
 */
const patientRenderer = (value: SimplePatient | undefined): string => {
  return personNameRenderer(value?.name);
};

/**
 * Renderer for DateTime values.
 * @param value the data value
 * @return the correct string value for the given value
 */

const dateTimeRenderer = (value: string | undefined): string => {
  if (isEmptyString(value)) {
    return getNotSpecified();
  }

  return DefaultDateTimeFormatter.formatDate(value);
};
/**
 * Renderer for Date values.
 * @param value the data value
 * @return the correct string value for the given value
 */
const dateRenderer = (value: string | undefined): string => {
  if (isEmptyString(value)) {
    return getNotSpecified();
  }

  return DefaultDateFormatter.formatDate(value);
};

/**
 * Renderer for values that are an array of GenCodes.
 *
 * @param value the data values
 * @return the correct string value for the given values
 */
const multipleGenCodesRenderer = (value: GenCode[] | undefined): string => {
  if (isEmptyArray(value)) {
    return getNotSpecified();
  }

  return value.map(val => genCodeRenderer(val)).join(', ');
};

/**
 * Renderer for regular boolean values.
 *
 * @param value the boolean value
 * @return the correct string value for the given boolean value
 */
const booleanRenderer = (value: boolean | undefined): string => {
  // specifically check for undefined to distinguish false and undefined
  if (value === undefined) {
    return getNotSpecified();
  }

  if (value) {
    return intl('view.button.yes');
  }

  return intl('view.button.no');
};

/**
 * Renderer for the document's Institution.
 *
 * @param value the institution value
 * @return the correct formatted string value for the given Institution
 */
const institutionRenderer = (value: Institution | undefined): string => {
  if (!value) {
    return getNotSpecified();
  }
  return value.name || value.oid || '';
};

/**
 * Renderer for a document's size value.
 *
 * @param value the size value to render
 * @return the correctly formatted size of a document
 */
const sizeRenderer = (value: number | undefined): string => {
  if (!value) {
    return getNotSpecified();
  }
  return convertNumberToFileSize(value);
};

/**
 * Renderer for a list of {@link ReferenceID}.
 *
 * @param value the list of referenceIDs to render
 * @return the correctly formatted list of reference ids as string.
 */
const referenceIDListRenderer = (value: ReferenceID[] | undefined): string => {
  if (!value) {
    return getNotSpecified();
  }

  return value.map(val => val.value || '').join(', ');
};

/**
 * Decodes HTML entities in a string. If the input is not a string,
 * it returns the input unchanged.
 *
 * @param text - The text to decode.
 * @returns - The decoded string if the input was a string, otherwise returns unchanged input.
 */
function decodeHtmlEntities(text: React.ReactNode) {
  if (typeof text === 'string') {
    const sanitized = DOMPurify.sanitize(text);
    const txt = document.createElement('textarea');
    txt.innerHTML = sanitized;
    return txt.value;
  }
  return text;
}

/**
 * Renders a row with the given label and renderedContent.
 *
 * @param label the row label
 * @param renderedContent the row content
 * @param rowKey optional 'key' property to pass to the created element
 * @return the created {@link React.ReactNode}
 */
const renderRow = (
  label: string,
  renderedContent: React.ReactNode,
  rowKey?: string
): React.ReactNode => {
  let cellBreak: string;
  if (renderedContent && renderedContent.toString().includes(' ')) {
    cellBreak = 'breakWord';
  } else {
    cellBreak = 'breakAll';
  }
  return (
    <div className="row" key={rowKey} role="row">
      <div className="row-header" role="rowheader">
        {label}
      </div>
      <div className={`row-cell ${cellBreak}`} role="cell">
        {decodeHtmlEntities(renderedContent)}
      </div>
    </div>
  );
};

/**
 * Renders a document's extraMetadata.
 *
 * @param value the extraMetadata values
 * @return the correctly formatted extraMetadata as ReactNode.
 */
const renderExtraMetaData = (
  value: ExtraMetadata[] | undefined
): React.ReactNode => {
  const titleText = intl('view.documentmetadata.title.tab.extra');

  if (isEmptyArray(value)) {
    return renderRow(titleText, getNotSpecified());
  }

  return (
    <div className="subsection">
      <div className="title">{titleText}</div>
      {value.map(val =>
        renderRow(
          val.key,
          isEmptyArray(val.values) ? getNotSpecified() : val.values.join(', '),
          val.key
        )
      )}
    </div>
  );
};

/**
 * Props for a {@link DocumentMetaData} component & {@link DocumentMetaDataPanel} component.
 */
export type RelatedDocumentsProps = {
  /**
   * Documents related to the document.
   */
  relatedDocuments?: JSX.Element;
  /**
   * Auto-scroll to related documents section.
   */
  scrollToRelatedDocuments?: boolean;
};

/**
 * Props for a {@link DocumentMetaData} component.
 */
export type DocumentMetaDataProps = {
  /**
   * The document to display the metadata for.
   */
  document: DocumentInformation | undefined;
  /**
   * The style to display the metadata for.
   */
  patientView?: boolean;
};

/**
 * Component for displaying the metadata of a {@link DocumentInformation} object.
 *
 * @param document the document to display the metadata for.
 * @param patientView the style to display the metadata for.
 * @param relatedDocuments documents related to @param document.
 * @param scrollToRelaredDocuments auto-scroll to realted documents section.
 * @param props leftover {@link DocumentMetaDataProps}
 * @return the created {@link JSX.Element}
 * @constructor
 */
export function DocumentMetaData({
  document,
  patientView,
  relatedDocuments,
  scrollToRelatedDocuments,
  ...props
}: Readonly<DocumentMetaDataProps & RelatedDocumentsProps>): JSX.Element {
  const [openIndex, setOpenIndex] = useState(0);
  const relatedDocumentsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollToRelatedDocuments) {
      if (relatedDocumentsRef.current) {
        // We'll use requestAnimationFrame because when the panel content is changing
        // and the scrolling is being calculated simultaneously, the scrolling ends up being incorrect.
        requestAnimationFrame(() => {
          relatedDocumentsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        });
      }
    }
  }, [document, scrollToRelatedDocuments]);

  const MedicalServiceData: JSX.Element = useMemo(() => {
    return (
      <>
        {renderRow(
          intl('view.documentmetadata.label.eventcodes'),
          multipleGenCodesRenderer(document?.eventCodes)
        )}
        {renderRow(
          intl('view.documentmetadata.label.practicesettingcode'),
          genCodeRenderer(document?.practiceSettingCode)
        )}
        {renderRow(
          intl('view.documentmetadata.label.legalauthenticator'),
          personNameRenderer(document?.legalAuthenticator)
        )}
        {renderRow(
          intl('view.documentmetadata.label.facilitycode'),
          genCodeRenderer(document?.healthcareFacilityTypeCode)
        )}
        {renderRow(
          intl('view.documentmetadata.label.institution'),
          institutionRenderer(document?.institution)
        )}
      </>
    );
  }, [
    document?.eventCodes,
    document?.healthcareFacilityTypeCode,
    document?.institution,
    document?.legalAuthenticator,
    document?.practiceSettingCode
  ]);

  const GeneralData: JSX.Element = useMemo(() => {
    return (
      <>
        {renderRow(
          intl('view.documentmetadata.label.comments'),
          dataRenderer(document?.comments)
        )}
        {renderRow(
          intl('view.documentmetadata.label.patient'),
          patientRenderer(document?.patient)
        )}
        {renderRow(
          intl('view.documentmetadata.label.start'),
          dateTimeRenderer(document?.serviceStartTime)
        )}
        {renderRow(
          intl('view.documentmetadata.label.stop'),
          dateTimeRenderer(document?.serviceStopTime)
        )}
        {renderRow(
          intl('view.documentmetadata.label.creationtime'),
          dateTimeRenderer(document?.creationTime)
        )}
        {renderRow(
          intl('view.documentmetadata.label.language'),
          languageCodeRenderer(document?.languageCode)
        )}
      </>
    );
  }, [
    document?.comments,
    document?.creationTime,
    document?.languageCode,
    document?.patient,
    document?.serviceStartTime,
    document?.serviceStopTime
  ]);

  const DocumentDetailsData: JSX.Element = useMemo(() => {
    return (
      <>
        {renderRow(
          intl('view.documentmetadata.label.formatcode'),
          genCodeRenderer(document?.formatCode)
        )}
        {renderRow(
          intl('view.documentmetadata.label.typecode'),
          genCodeRenderer(document?.typeCode)
        )}
        {renderRow(
          intl('view.documentmetadata.label.classcode'),
          genCodeRenderer(document?.classCode)
        )}
        {renderRow(
          intl('view.documentmetadata.label.dynamicdocument'),
          booleanRenderer(document?.dynamicDocument)
        )}
        {renderRow(
          intl('view.documentmetadata.label.size'),
          sizeRenderer(document?.size)
        )}
        {renderRow(
          intl('view.documentmetadata.label.mimetype'),
          dataRenderer(document?.mimeType)
        )}
        {renderRow(
          intl('view.documentmetadata.label.referenceidlist'),
          referenceIDListRenderer(document?.referenceIDList)
        )}
        {renderExtraMetaData(document?.extraMetadata)}
      </>
    );
  }, [
    document?.classCode,
    document?.dynamicDocument,
    document?.extraMetadata,
    document?.formatCode,
    document?.mimeType,
    document?.referenceIDList,
    document?.size,
    document?.typeCode
  ]);

  const DocumentIdentifiersData: JSX.Element = useMemo(() => {
    return (
      <>
        {renderRow(
          intl('view.documentmetadata.label.confidentialitycodes'),
          multipleGenCodesRenderer(document?.confidentialityCodes)
        )}
        {renderRow(
          intl('view.documentmetadata.label.repositoryid'),
          dataRenderer(document?.repositoryID)
        )}
        {renderRow(
          intl('view.documentmetadata.label.homecommunityid'),
          dataRenderer(document?.homeCommunityID)
        )}
        {renderRow(
          intl('view.documentmetadata.label.entryuuid'),
          dataRenderer(document?.entryUUID)
        )}
        {renderRow(
          intl('view.documentmetadata.label.uniqueid'),
          dataRenderer(document?.uniqueID)
        )}
        {renderRow(
          intl('view.documentmetadata.label.logicalid'),
          dataRenderer(document?.logicalID)
        )}
        {renderRow(
          intl('view.documentmetadata.label.version'),
          dataRenderer(document?.version)
        )}
      </>
    );
  }, [
    document?.confidentialityCodes,
    document?.entryUUID,
    document?.homeCommunityID,
    document?.logicalID,
    document?.repositoryID,
    document?.uniqueID,
    document?.version
  ]);

  const PatientView: JSX.Element = useMemo(() => {
    return (
      <>
        {/*General*/}
        <ShuiTile title={intl('view.documentmetadata.title.tab.general')}>
          {GeneralData}
        </ShuiTile>

        {/*Medical Service Related*/}
        <ShuiTile title={intl('view.documentmetadata.title.tab.medical')}>
          {MedicalServiceData}
        </ShuiTile>

        {/*Document Details*/}
        <ShuiTile title={intl('view.documentmetadata.title.tab.details')}>
          {DocumentDetailsData}
        </ShuiTile>

        {/*Document Identifiers*/}
        <ShuiTile title={intl('view.documentmetadata.title.tab.identifiers')}>
          {DocumentIdentifiersData}
        </ShuiTile>
        {/*Related Documents*/}
        {relatedDocuments && (
          <ShuiTile
            title={intl('view.documentmetadata.title.tab.relatedDocuments')}
            tileTopRef={relatedDocumentsRef}
            childrenClassName="linked-documents"
          >
            {relatedDocuments}
          </ShuiTile>
        )}
      </>
    );
  }, [
    GeneralData,
    MedicalServiceData,
    DocumentDetailsData,
    DocumentIdentifiersData,
    relatedDocuments
  ]);

  const DefaultView: JSX.Element = useMemo(() => {
    return (
      <>
        {/*General*/}
        <Collapsible
          triggerText={intl('view.documentmetadata.title.tab.general')}
          aria-label={intl('view.documentmetadata.title.tab.general')}
          contentRole="table"
          key="general"
          grouped={{
            index: 0,
            currentOpenIndex: openIndex,
            setOpenIndex: setOpenIndex
          }}
        >
          {GeneralData}
        </Collapsible>

        {/*Medical Service Related*/}
        <Collapsible
          triggerText={intl('view.documentmetadata.title.tab.medical')}
          aria-label={intl('view.documentmetadata.title.tab.medical')}
          contentRole="table"
          key="medical-service-related"
          grouped={{
            index: 1,
            currentOpenIndex: openIndex,
            setOpenIndex: setOpenIndex
          }}
        >
          {MedicalServiceData}
        </Collapsible>

        {/*Document Details*/}
        <Collapsible
          triggerText={intl('view.documentmetadata.title.tab.details')}
          aria-label={intl('view.documentmetadata.title.tab.details')}
          contentRole="table"
          key="document-details"
          grouped={{
            index: 2,
            currentOpenIndex: openIndex,
            setOpenIndex: setOpenIndex
          }}
        >
          {DocumentDetailsData}
        </Collapsible>

        {/*Document Identifiers*/}
        <Collapsible
          triggerText={intl('view.documentmetadata.title.tab.identifiers')}
          aria-label={intl('view.documentmetadata.title.tab.identifiers')}
          contentRole="table"
          key="document-identifiers"
          grouped={{
            index: 3,
            currentOpenIndex: openIndex,
            setOpenIndex: setOpenIndex
          }}
        >
          {DocumentIdentifiersData}
        </Collapsible>
      </>
    );
  }, [
    DocumentDetailsData,
    DocumentIdentifiersData,
    GeneralData,
    MedicalServiceData,
    openIndex
  ]);
  const documentType = patientView
    ? 'ehs-shui-document-metadata'
    : 'ehs-document-metadata';
  return (
    <div {...props} className={documentType}>
      {patientView ? PatientView : DefaultView}
    </div>
  );
}

/**
 * Props for a {@link DocumentMetaDataPanel}.
 */
export type DocumentMetaDataPanelProps = DistributiveOmit<
  PanelProps,
  'children'
> & {
  /**
   * The document to display the metadata for.
   */
  document: DocumentInformation | undefined;
  /**
   * Handler for the open action button of the panel. The open action will only be displayed if a handler is passed.
   */
  onOpen?: VoidCallback;
  /**
   * If the data in the panel is `dirty`.
   */
  dirty?: boolean;
  /**
   * The message that will be displayed if the data in the panel is dirty.
   */
  dirtyMessage?: string;
  /**
   * The switcher to render content based on user.
   */
  patientView?: boolean;
};

/**
 * Panel to display the meta data of a {@link DocumentInformation} object.
 *
 * @param properties the {@link DocumentMetaDataPanelProps}
 * @return the created {@link JSX.Element}.
 * @constructor
 */
export const DocumentMetaDataPanel = forwardRef(function DocumentMetaDataPanel(
  {
    document,
    onOpen,
    tabbableContent,
    relatedDocuments,
    dirty = false,
    dirtyMessage = intl('view.documentmetadata.warning.differentinviewer'),
    patientView = false,
    scrollToRelatedDocuments = false,
    ...props
  }: Readonly<DocumentMetaDataPanelProps & RelatedDocumentsProps>,
  ref: Ref<HTMLDivElement>
): JSX.Element {
  // create panel actions
  let actions;
  const isMobileView = useBreakpoint(Breakpoints.MOBILE);
  const panelTopRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!scrollToRelatedDocuments) {
      if (panelTopRef.current) {
        panelTopRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [document, ref, scrollToRelatedDocuments]);

  if (patientView && isMobileView && props.closable) {
    actions = (
      <div className={'back-button'}>
        <Tool
          icon="materialicon-chevron_left"
          onClick={props.closable.onClose}
          tooltip={intl('view.button.close')}
        />
        <div className="back-button-title">
          <span className={'back-button-text'}>{intl('common.details')}</span>
        </div>
      </div>
    );
  } else if (onOpen) {
    actions = (
      <Button
        variant="primary"
        onClick={onOpen}
        text={intl('view.button.open')}
      />
    );
  }
  return (
    <Panel
      {...props}
      ref={ref}
      actions={actions}
      title={
        !(isMobileView && patientView)
          ? props.title ?? document?.title ?? getNotSpecified()
          : undefined
      }
      className={`ehs-doc-metadata-panel ${props.className ?? ''} ${
        patientView ? 'patient' : ''
      }`.trim()}
      closable={!(isMobileView && patientView) ? props.closable : undefined}
      tabbableContent={tabbableContent}
    >
      <div ref={panelTopRef} />
      {document?.availabilityStatus?.toLowerCase() === 'deprecated' && (
        // "alert-info" is a class defined in the EHealthTheme project,
        // which will style this for us
        <div className="alert-info" role="alert">
          {intl('personalstorage.view.documentmetadata.deprecated')}
        </div>
      )}
      {isMobileView && patientView && <div className="panelTitle-spacer" />}
      {dirty && (
        <div className="alert-info" role="alert">
          {dirtyMessage}
        </div>
      )}
      {patientView ? (
        <>
          {isMobileView && (
            <h2 className="panelTitle-text">
              {document?.title ?? getNotSpecified()}
            </h2>
          )}
          <div className="file-time-author">
            {dateRenderer(document?.creationTime)}
            {personNameRenderer(document?.author) && (
              <>
                {', '} {personNameRenderer(document?.author)}
              </>
            )}
          </div>
        </>
      ) : (
        renderRow(
          intl('view.documentmetadata.label.author'),
          personNameRenderer(document?.author)
        )
      )}

      <DocumentMetaData
        document={document}
        patientView={patientView}
        relatedDocuments={relatedDocuments}
        scrollToRelatedDocuments={scrollToRelatedDocuments}
      />
    </Panel>
  );
});

export interface ShuiTileProps extends Required<ChildrenProp>, ClassNameProp {
  /**
   * Text of the collapse/expand trigger. Can also be an JSX element.
   */
  title: string | JSX.Element;
  /**
   * ID of the element to scrollTo.
   */
  tileTopRef?: MutableRefObject<HTMLDivElement | null>;
  /**
   * Children element class name.
   */
  childrenClassName?: string /* 'linked-documents' */;
}

export function ShuiTile({
  title,
  tileTopRef,
  childrenClassName,
  children,
  ...props
}: Readonly<ShuiTileProps>): JSX.Element {
  return (
    <div {...props} className="metadata-title-wrapper" ref={tileTopRef}>
      <h3 className="metadata-title"> {title} </h3>
      <div className={childrenClassName ? childrenClassName : 'shui-content'}>
        {children}
      </div>
    </div>
  );
}
